import { useState } from "react";
import { omit } from "lodash";
import {
  ModalManager,
  useModal,
} from "@/react/components/Layout/ModalManagerProvider";
import type { CoursesDirectory } from "@/react/types/CoursesDirectory";
import { FullScreenModal } from "@circle-react-shared/ModalsV2/FullScreenModal";
import { Form } from "@circle-react-uikit/Form";
import { ModalBody } from "./ModalBody";
import { ModalFooter } from "./ModalFooter";
import { ModalHeader } from "./ModalHeader";
import { useShowCoursesDirectory } from "./hooks/useShowCoursesDirectory";
import { useUpdateCoursesDirectory } from "./hooks/useUpdateCoursesDirectory";

export const COURSES_DIRECTORY_FORM_ID = "courses-directory-form";

export type TabOptions = "banner" | "options";
interface OptionsModalComponentProps {
  tab: TabOptions;
}
export const OptionsModalComponent = ({ tab }: OptionsModalComponentProps) => {
  const { visible: isVisible, remove } = useModal();
  const [selectedTab, setSelectedTab] = useState<TabOptions>(tab);
  const { data, isLoading } = useShowCoursesDirectory();
  const { mutate, isUpdating } = useUpdateCoursesDirectory();
  const handleSubmit = (payload: CoursesDirectory) => {
    const processedPayload = {
      ...omit(payload, "courses"),
      courses_order: payload.courses.map(course => course.id.toString()),
      logged_in_banner_image: payload.logged_in_banner_image?.signed_id || null,
      logged_out_banner_image:
        payload.logged_out_banner_image?.signed_id || null,
    };
    mutate(processedPayload);
  };

  if (isLoading || !data) {
    return null;
  }

  return (
    <Form
      id={COURSES_DIRECTORY_FORM_ID}
      defaultValues={data}
      preventAccidentalLeave
      onSubmit={handleSubmit}
    >
      <FullScreenModal
        isOpen={isVisible}
        onClose={remove}
        shouldShowCloseButton
        contentPortalId="options-modal-content-portal"
      >
        <FullScreenModal.Header className="!py-4 md:!py-5">
          <ModalHeader
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            onClose={remove}
          />
        </FullScreenModal.Header>
        <FullScreenModal.Body wrapperClassName="h-full flex justify-center">
          <ModalBody selectedTab={selectedTab} />
        </FullScreenModal.Body>
        <FullScreenModal.Footer>
          <ModalFooter isUpdating={isUpdating} onCancel={remove} />
        </FullScreenModal.Footer>
      </FullScreenModal>
    </Form>
  );
};

const OptionsModal = ModalManager.create(
  (props: OptionsModalComponentProps) => <OptionsModalComponent {...props} />,
);

export const useOptionsModal = () => useModal(OptionsModal);
